import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, LogOut, Upload, ChevronLeft, ChevronRight, X } from "lucide-react";
import { api } from "@/lib/api";
import { Markdown } from "@/components/Markdown";
import { ContentManager } from "@/pages/ContentManager";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { title: "", type: "articolo", tag: "", date: "", excerpt: "", body: "", image: "", images: [], published: true };
const input = "w-full bg-transparent border border-darkgrey/40 px-4 py-3 font-body focus:outline-none focus:border-ink";

function LoginView() {
  const { login, checkAuth } = useAuth();
  const [c, setC] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(c.email, c.password); await checkAuth(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Credenziali non valide"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-greige px-6">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
        <p className="font-mono2 text-xs uppercase tracking-widest text-darkgrey mb-2">2e Lab · Back office</p>
        <h1 className="font-heading text-4xl mb-10">Area amministratore</h1>
        <input data-testid="admin-email" type="email" required placeholder="Email" value={c.email}
          onChange={(e) => setC({ ...c, email: e.target.value })} className={input + " mb-4"} />
        <input data-testid="admin-password" type="password" required placeholder="Password" value={c.password}
          onChange={(e) => setC({ ...c, password: e.target.value })} className={input + " mb-6"} />
        <button data-testid="admin-login-submit" disabled={busy}
          className="w-full bg-ink text-greige py-4 uppercase text-xs tracking-widest hover:bg-darkgrey transition-colors disabled:opacity-50">
          {busy ? "Accesso…" : "Accedi"}
        </button>
        <Link to="/" className="block text-center font-mono2 text-[11px] uppercase tracking-widest text-darkgrey mt-6 hover:text-ink">← Torna al sito</Link>
      </form>
    </div>
  );
}

function PostForm({ initial, onSaved, onCancel }) {
  const [f, setF] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const bodyRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const applyWrap = (pre, post = pre) => {
    const ta = bodyRef.current;
    if (!ta) return;
    const s = ta.selectionStart, e = ta.selectionEnd, val = f.body || "";
    const sel = val.slice(s, e) || "testo";
    setF((p) => ({ ...p, body: val.slice(0, s) + pre + sel + post + val.slice(e) }));
    requestAnimationFrame(() => { const t = bodyRef.current; if (!t) return; t.focus(); t.selectionStart = s + pre.length; t.selectionEnd = s + pre.length + sel.length; });
  };
  const applyLine = (prefix) => {
    const ta = bodyRef.current;
    if (!ta) return;
    const s = ta.selectionStart, val = f.body || "";
    const ls = val.lastIndexOf("\n", s - 1) + 1;
    setF((p) => ({ ...p, body: val.slice(0, ls) + prefix + val.slice(ls) }));
    requestAnimationFrame(() => { const t = bodyRef.current; if (!t) return; t.focus(); t.selectionStart = t.selectionEnd = s + prefix.length; });
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, image: `${BACKEND}${data.url}` }));
      toast.success("Immagine caricata");
    } catch { toast.error("Upload non riuscito"); }
    finally { setUploading(false); }
  };

  const uploadSlides = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const url = `${BACKEND}${data.url}`;
        setF((prev) => ({ ...prev, images: [...(prev.images || []), url] }));
      }
      toast.success(files.length > 1 ? "Immagini caricate" : "Immagine caricata");
    } catch { toast.error("Upload non riuscito"); }
    finally { setUploading(false); if (e.target) e.target.value = ""; }
  };

  const moveSlide = (idx, dir) => setF((p) => {
    const arr = [...(p.images || [])], j = idx + dir;
    if (j < 0 || j >= arr.length) return p;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...p, images: arr };
  });
  const removeSlide = (idx) => setF((p) => ({ ...p, images: (p.images || []).filter((_, i) => i !== idx) }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (f.id) await api.put(`/posts/${f.id}`, f);
      else await api.post("/posts", f);
      toast.success("Salvato");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} data-testid="admin-post-form" className="space-y-5 border border-darkgrey/30 p-6 md:p-8 bg-paper/40">
      <h2 className="font-heading text-2xl">{f.id ? "Modifica contenuto" : "Nuovo contenuto"}</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="md:col-span-1"><span className="font-mono2 text-[11px] uppercase text-darkgrey">Tipo</span>
          <select data-testid="post-type" value={f.type} onChange={set("type")} className={input}>
            <option value="articolo">Articolo</option>
            <option value="slide">Design Slide</option>
          </select>
        </label>
        <label className="md:col-span-1"><span className="font-mono2 text-[11px] uppercase text-darkgrey">Tag</span>
          <input data-testid="post-tag" value={f.tag} onChange={set("tag")} placeholder="PUNTI DI VISTA" className={input} />
        </label>
        <label className="md:col-span-1"><span className="font-mono2 text-[11px] uppercase text-darkgrey">Data</span>
          <input data-testid="post-date" value={f.date} onChange={set("date")} placeholder="19-06-2026" className={input} />
        </label>
      </div>
      <label className="block"><span className="font-mono2 text-[11px] uppercase text-darkgrey">Titolo</span>
        <input data-testid="post-title" required value={f.title} onChange={set("title")} className={input} />
      </label>
      <label className="block"><span className="font-mono2 text-[11px] uppercase text-darkgrey">Estratto (anteprima)</span>
        <textarea data-testid="post-excerpt" rows={2} value={f.excerpt} onChange={set("excerpt")} className={input} />
      </label>
      {f.type === "articolo" && (
        <div>
          <span className="font-mono2 text-[11px] uppercase text-darkgrey">Testo articolo</span>
          <div className="flex flex-wrap gap-2 mt-2 mb-2" data-testid="editor-toolbar">
            <button type="button" onClick={() => applyWrap("**")} title="Grassetto" className="border border-darkgrey/40 px-3 py-1.5 font-mono2 text-xs hover:bg-ink hover:text-greige transition-colors"><b>B</b></button>
            <button type="button" onClick={() => applyWrap("*")} title="Corsivo" className="border border-darkgrey/40 px-3 py-1.5 font-mono2 text-xs italic hover:bg-ink hover:text-greige transition-colors">I</button>
            <button type="button" onClick={() => applyLine("## ")} title="Titolo" className="border border-darkgrey/40 px-3 py-1.5 font-mono2 text-xs hover:bg-ink hover:text-greige transition-colors">H</button>
            <button type="button" onClick={() => applyLine("> ")} title="Citazione" className="border border-darkgrey/40 px-3 py-1.5 font-mono2 text-xs hover:bg-ink hover:text-greige transition-colors">&ldquo;&rdquo;</button>
            <button type="button" onClick={() => applyLine("- ")} title="Elenco" className="border border-darkgrey/40 px-3 py-1.5 font-mono2 text-xs hover:bg-ink hover:text-greige transition-colors">&bull; Lista</button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <textarea ref={bodyRef} data-testid="post-body" rows={16} value={f.body} onChange={set("body")}
              placeholder="Scrivi qui. Seleziona il testo e usa i pulsanti per grassetto, corsivo, citazioni, titoli ed elenchi. Lascia una riga vuota tra i paragrafi." className={input + " font-mono2 text-sm leading-relaxed"} />
            </div>
            <div className="border border-darkgrey/30 bg-paper p-5 overflow-auto" style={{ maxHeight: 520 }} data-testid="editor-preview">
              <p className="font-mono2 text-[10px] uppercase tracking-widest text-darkgrey mb-4 pb-2 border-b border-darkgrey/20">Anteprima live</p>
              {f.body ? <Markdown>{f.body}</Markdown> : <p className="font-editorial text-xl text-darkgrey">L'anteprima comparirà qui mentre scrivi.</p>}
            </div>
          </div>
          <p className="font-mono2 text-[11px] text-darkgrey mt-2">Formattazione Markdown: **grassetto**, *corsivo*, ## titolo, &gt; citazione, - elenco.</p>
        </div>
      )}
      {f.type === "slide" ? (
        <div data-testid="slide-images-section">
          <span className="font-mono2 text-[11px] uppercase text-darkgrey">Immagini slide · scorrono con le frecce</span>
          <div className="flex items-center gap-4 mt-2">
            <label className="inline-flex items-center gap-2 border border-ink px-4 py-2 uppercase text-xs tracking-widest cursor-pointer hover:bg-ink hover:text-greige transition-colors">
              <Upload size={14} /> {uploading ? "Carico…" : "Aggiungi immagini"}
              <input data-testid="slide-images-upload" type="file" accept="image/*" multiple onChange={uploadSlides} className="hidden" />
            </label>
            <span className="font-mono2 text-[11px] text-darkgrey">{(f.images || []).length} immagini</span>
          </div>
          {(f.images || []).length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-4">
              {f.images.map((src, idx) => (
                <div key={idx} className="relative border border-darkgrey/30 bg-paper group">
                  <img src={src} alt={`slide ${idx + 1}`} className="w-full h-28 object-contain" />
                  <span className="absolute top-1 left-1 font-mono2 text-[10px] bg-ink text-greige px-1.5">{idx + 1}</span>
                  <div className="absolute inset-x-0 bottom-0 flex justify-between bg-ink/85 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button type="button" onClick={() => moveSlide(idx, -1)} disabled={idx === 0} className="text-greige px-2 py-1 disabled:opacity-30" title="Sposta a sinistra"><ChevronLeft size={14} /></button>
                    <button type="button" data-testid={`slide-remove-${idx}`} onClick={() => removeSlide(idx)} className="text-greige px-2 py-1 hover:text-glitch-red" title="Rimuovi"><X size={14} /></button>
                    <button type="button" onClick={() => moveSlide(idx, 1)} disabled={idx === f.images.length - 1} className="text-greige px-2 py-1 disabled:opacity-30" title="Sposta a destra"><ChevronRight size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          <span className="font-mono2 text-[11px] uppercase text-darkgrey">Immagine di copertina</span>
          <div className="flex items-center gap-4 mt-2">
            <label className="inline-flex items-center gap-2 border border-ink px-4 py-2 uppercase text-xs tracking-widest cursor-pointer hover:bg-ink hover:text-greige transition-colors">
              <Upload size={14} /> {uploading ? "Carico…" : "Carica"}
              <input data-testid="post-image-upload" type="file" accept="image/*" onChange={upload} className="hidden" />
            </label>
            {f.image && <img src={f.image} alt="preview" className="h-16 w-auto border border-darkgrey/30" />}
          </div>
          <input data-testid="post-image-url" value={f.image} onChange={set("image")} placeholder="oppure incolla un URL immagine" className={input + " mt-3"} />
        </div>
      )}
      <label className="flex items-center gap-3 cursor-pointer pt-2">
        <input type="checkbox" data-testid="post-published" checked={f.published}
          onChange={(e) => setF({ ...f, published: e.target.checked })} className="w-4 h-4 accent-black" />
        <span className="font-mono2 text-xs uppercase tracking-widest text-darkgrey">
          {f.published ? "Pubblicato · visibile sul sito" : "Bozza · non visibile"}
        </span>
      </label>
      <div className="flex gap-3 pt-2">
        <button data-testid="post-save" disabled={busy} className="bg-ink text-greige px-6 py-3 uppercase text-xs tracking-widest hover:bg-darkgrey transition-colors disabled:opacity-50">
          {busy ? "Salvo…" : "Salva"}
        </button>
        <button type="button" onClick={onCancel} className="border border-darkgrey/50 px-6 py-3 uppercase text-xs tracking-widest hover:bg-ink hover:text-greige transition-colors">
          Annulla
        </button>
      </div>
    </form>
  );
}

export default function Admin() {
  const { user, loading, logout, checkAuth } = useAuth();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | post
  const [tab, setTab] = useState("testi"); // testi | blog

  const load = useCallback(() => {
    api.get("/posts/all").then(({ data }) => setPosts(data)).catch(() => {});
  }, []);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);
  useEffect(() => { if (!loading && user && user.is_admin === undefined) checkAuth(); }, [loading, user, checkAuth]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-greige"><span className="font-mono2 text-xs uppercase text-darkgrey animate-pulse">…</span></div>;
  if (!user) return <LoginView />;
  if (!user.is_admin) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-greige gap-4">
      <p className="font-editorial text-2xl text-darkgrey">Questo account non ha i permessi di amministratore.</p>
      <button onClick={logout} className="font-mono2 text-xs uppercase tracking-widest link-underline">Esci</button>
    </div>
  );

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo contenuto?")) return;
    await api.delete(`/posts/${id}`);
    toast.success("Eliminato");
    load();
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-greige pt-12 pb-24 px-6 md:px-12">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="font-mono2 text-xs uppercase tracking-widest text-darkgrey">2e Lab · Editoriale</p>
            <h1 className="font-heading text-4xl">Gestione contenuti</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/editoriale" className="font-mono2 text-xs uppercase tracking-widest link-underline">Vedi il blog</Link>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 font-mono2 text-xs uppercase tracking-widest text-darkgrey hover:text-glitch-red">
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-10 border-b border-darkgrey/30">
          <button data-testid="tab-testi" onClick={() => setTab("testi")}
            className={`px-1 pb-3 -mb-px font-mono2 text-xs uppercase tracking-widest border-b-2 transition-colors ${tab === "testi" ? "border-ink text-ink" : "border-transparent text-darkgrey hover:text-ink"}`}>
            Testi del sito
          </button>
          <button data-testid="tab-blog" onClick={() => setTab("blog")}
            className={`ml-6 px-1 pb-3 -mb-px font-mono2 text-xs uppercase tracking-widest border-b-2 transition-colors ${tab === "blog" ? "border-ink text-ink" : "border-transparent text-darkgrey hover:text-ink"}`}>
            Editoriale (Blog)
          </button>
        </div>

        {tab === "testi" ? (
          <ContentManager />
        ) : editing !== null ? (
          <PostForm initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
        ) : (
          <>
            <button data-testid="admin-new-post" onClick={() => setEditing({ ...EMPTY })}
              className="inline-flex items-center gap-2 bg-ink text-greige px-6 py-3 uppercase text-xs tracking-widest hover:bg-darkgrey transition-colors mb-8">
              <Plus size={16} /> Nuovo contenuto
            </button>
            <div className="border-t border-darkgrey/30">
              {posts.length === 0 && <p className="py-8 font-editorial text-xl text-darkgrey">Nessun contenuto. Inizia con "Nuovo contenuto".</p>}
              {posts.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-4 py-5 border-b border-darkgrey/30">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="font-mono2 text-[10px] uppercase tracking-widest bg-ink text-greige px-2 py-0.5">{p.type}</span>
                      <span className="font-mono2 text-[11px] text-darkgrey">{p.tag} · {p.date}</span>
                      {!p.published && <span className="font-mono2 text-[10px] uppercase text-glitch-red">bozza</span>}
                    </div>
                    <p className="font-heading text-xl mt-1 truncate">{p.title}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`edit-${p.slug}`} onClick={() => setEditing(p)} className="border border-ink p-2 hover:bg-ink hover:text-greige transition-colors"><Pencil size={15} /></button>
                    <button data-testid={`delete-${p.slug}`} onClick={() => remove(p.id)} className="border border-darkgrey/50 p-2 hover:bg-glitch-red hover:text-white hover:border-glitch-red transition-colors"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
